import React from 'react';
import { Sparkles, Users, Heart, ArrowRight, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ArenaRecommendation } from '@/hooks/useArenaTopics';
import { cn } from '@/lib/utils';

interface ArenaRecommendationsProps {
  recommendations: ArenaRecommendation[];
  onJoinArena: (arenaId: string) => void;
  loading?: boolean;
  compact?: boolean;
}

export const ArenaRecommendations: React.FC<ArenaRecommendationsProps> = ({
  recommendations,
  onJoinArena,
  loading = false,
  compact = false,
}) => {
  // Filter to only show recommendations with some match
  const relevantRecommendations = recommendations.filter(
    (r) => r.match_score > 0 || r.matching_interests.length > 0
  );

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary animate-pulse" />
          <span className="text-sm font-medium">For You</span>
        </div>
        <div className="flex gap-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-32 w-48 rounded-xl bg-muted animate-pulse flex-shrink-0"
            />
          ))}
        </div>
      </div>
    );
  }

  if (relevantRecommendations.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Recommended for You</span>
        </div>
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex gap-2 pb-2">
            {relevantRecommendations.slice(0, 5).map((rec) => (
              <button
                key={rec.arena_id}
                onClick={() => onJoinArena(rec.arena_id)}
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 hover:border-primary/50 transition-all hover:scale-105"
              >
                <span className="text-sm font-medium truncate max-w-[120px]">
                  {rec.arena_name}
                </span>
                <Badge variant="secondary" className="text-xs">
                  {rec.match_score}%
                </Badge>
              </button>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Recommended for You</span>
        </div>
        <Badge variant="outline" className="text-xs">
          Based on your interests
        </Badge>
      </div>

      <ScrollArea className="w-full whitespace-nowrap">
        <div className="flex gap-3 pb-2">
          {relevantRecommendations.map((rec) => (
            <RecommendationCard
              key={rec.arena_id}
              recommendation={rec}
              onJoin={() => onJoinArena(rec.arena_id)}
            />
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};

interface RecommendationCardProps {
  recommendation: ArenaRecommendation;
  onJoin: () => void;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  onJoin,
}) => {
  const { arena_name, arena_topic, match_score, matching_interests, participant_count } =
    recommendation;

  const getMatchColor = (score: number) => {
    if (score >= 70) return 'from-green-500 to-emerald-500';
    if (score >= 40) return 'from-amber-500 to-orange-500';
    return 'from-blue-500 to-indigo-500';
  };

  return (
    <Card className="w-56 flex-shrink-0 overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1">
      <div
        className={cn(
          'h-2 bg-gradient-to-r',
          getMatchColor(match_score)
        )}
      />
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm truncate">{arena_name}</h4>
            <p className="text-xs text-muted-foreground truncate">{arena_topic}</p>
          </div>
          <div
            className={cn(
              'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold text-white bg-gradient-to-r',
              getMatchColor(match_score)
            )}
          >
            <Zap className="w-3 h-3" />
            {match_score}%
          </div>
        </div>

        {matching_interests.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {matching_interests.slice(0, 3).map((interest, i) => (
              <Badge key={i} variant="secondary" className="text-xs py-0">
                <Heart className="w-2.5 h-2.5 mr-0.5 text-pink-500" />
                {interest}
              </Badge>
            ))}
            {matching_interests.length > 3 && (
              <Badge variant="outline" className="text-xs py-0">
                +{matching_interests.length - 3}
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="w-3 h-3" />
            <span>{participant_count} inside</span>
          </div>
          <Button size="sm" className="h-7 text-xs gap-1" onClick={onJoin}>
            Join
            <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ArenaRecommendations;
